<template>
  <ui-modal-layout
    :title="tr('Edit budget')"
    :description="tr('Proper budget management is the foundation of successful advertising. Here you can adjust your spending limit to ensure it perfectly aligns with your broader strategy and business goals.')"
    :icon="WalletIcon"
    @keydown.enter.prevent="onSave()"
  >
    <div class="flex flex-col gap-1.5">
      <ui-label>{{ budgetTypeLabel }}</ui-label>
      <budget-editor
        ref="budgetEditor"
        v-model="localBudget"
        :period="period"
        :level="budgetLevel"
        :currency-code="rule.adAccountCurrency ? rule.adAccountCurrency.code : 'EUR'"
        :ad-account-currency-symbol="rule.adAccountCurrency ? rule.adAccountCurrency.symbol : null"
        :ad-account-id="rule.ad_account ? rule.ad_account.id : null"
        :min-daily-budget="rule.adAccountMinDailyBugdet"
        :disabled="isSaving"
        :period-disabled="true"
        :show-available-ads="isCampaignBudget"
        :lifetime-start-time="rule.settings ? rule.settings.adset_start_time : null"
        :lifetime-end-time="rule.settings ? rule.settings.adset_end_time : null"
        :lifetime-lifespan-max-days="rule.settings ? rule.settings.adset_lifespan_max_days : null"
      />
    </div>

    <template #footer>
      <ui-button variant="outline" :disabled="isSaving" @click="() => close()">
        {{ tr('Cancel') }}
      </ui-button>
      <ui-button :disabled="isSaving || isInvalid" @click="() => onSave()">
        <loader-circle-icon v-if="isSaving" class="animate-spin" />
        {{ tr('Save') }}
      </ui-button>
    </template>
  </ui-modal-layout>
</template>

<script>
import { LoaderCircle, Wallet } from '@lucide/vue';
import { markRaw } from 'vue';

import Rule from '@/entities/Rule.js';
import RuleService from '@/services/Rule.js';
import BudgetEditor from '@/ui/components/BudgetEditor.vue';

export default {
  name: 'RuleBudgetEditModalContent',
  components: {
    BudgetEditor,
    LoaderCircleIcon: LoaderCircle,
  },
  props: {
    rule: {
      type: Rule,
      required: true,
    },
  },
  data: () => ({
    WalletIcon: markRaw(Wallet),
    editorRef: null,
    isSaving: false,
    localBudget: null,
  }),
  computed: {
    isCampaignBudget() {
      return !!(this.rule.output_campaign && this.rule.output_campaign.budget);
    },
    period() {
      if (this.isCampaignBudget) {
        return this.rule.output_campaign.budget_period || 'daily';
      }

      return this.rule.settings?.adset_budget_period || 'daily';
    },
    budgetTypeLabel() {
      return this.isCampaignBudget
        ? this.tr('Campaign budget')
        : this.tr('Adset budget');
    },
    budgetLevel() {
      return this.isCampaignBudget ? 'campaign' : 'adset';
    },
    isInvalid() {
      if (!this.editorRef) {
        return true;
      }
      return this.editorRef.invalidBudget || this.editorRef.isBudgetRequestRunning;
    },
  },
  created() {
    this.localBudget = this.isCampaignBudget
      ? this.rule.output_campaign.budget
      : this.rule.settings?.adset_budget;
  },
  mounted() {
    this.editorRef = this.$refs.budgetEditor;
    this.$nextTick(() => {
      this.editorRef?.focus();
    });
  },
  methods: {
    close() {
      this.$fullscreenModal.closeAll();
    },
    onSave() {
      if (this.isSaving || this.isInvalid) {
        return;
      }
      const value = parseFloat(this.localBudget);
      if (Number.isNaN(value) || value <= 0) {
        return;
      }

      this.$toast.clear();
      this.isSaving = true;
      RuleService.updateBudget(this.rule.id, value)
        .then(updatedRule => {
          this.rule.updateWith(updatedRule);
          this.$store.commit('rules/updateInList', this.rule);
          this.$toast.success(this.tr('Budget updated successfully.'));
          this.close();

          return updatedRule;
        })
        .catch(err => this.error(err))
        .finally(() => this.isSaving = false);
    },
  },
};
</script>
